import { Box, Button, Grid, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import {
	ArrayInput,
	BooleanInput,
	DateInput,
	FormDataConsumer,
	useRecordContext,
	ImageField,
	ImageInput,
	NumberInput,
	ReferenceArrayInput,
	AutocompleteArrayInput,
	SelectArrayInput,
	SimpleFormIterator,
	TextInput,
} from 'react-admin';
import { GiCancel } from 'react-icons/gi';
import constants from '../../constants';
import UsageBenchmarkGauge from './UsageBenchmarkGauge';

const SafeSpaceResetField = () => {
	const record = useRecordContext();
	const reset = record?.safe_space_last_reset;
	if (reset) {
		return (
			<Box sx={{ mt: 1 }}>
				<Typography variant="caption" color="textSecondary" display="block">
					תאריך איפוס אחרון (מרחב בטוח)
				</Typography>
				<Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1d3a8a' }}>
					{moment(reset).format('DD/MM/YYYY')}
				</Typography>
			</Box>
		);
	}
	return (
		<Box
			sx={{
				mt: 1,
				p: 1.5,
				border: '2px solid #d32f2f',
				borderRadius: 1,
				backgroundColor: '#fdecea',
			}}
		>
			<Typography sx={{ fontWeight: 700, fontSize: 16, color: '#d32f2f' }}>
				אין כאן תאריך איפוס — נא לשים לב שהחברה לא מסבסדת
			</Typography>
		</Box>
	);
};

const GeneralCompanyInfo = ({
	udiFactors,
	record,
	refetch,
	onAddUdiFactorsButtonPress,
}: {
	udiFactors: Map<string, { value: number; updatedAt: Date; active: boolean }>;
	record: any;
	refetch: () => void;
	onAddUdiFactorsButtonPress: () => void;
}) => {
	return (
		<Grid container justifyContent="flex-start" spacing={2}>
			<Grid item xs={12} textAlign={'start'}>
				<TextInput label="שם חברה" source="english_name" />
			</Grid>

			<Box width="100%" />
			<Grid item sm={3} alignContent={'center'}>
				<ImageField source="logo" sx={{}} />
			</Grid>
			<Grid item sm={3} alignContent={'center'}>
				<TextInput source="logo_background" label="רקע לוגו" type="color" style={{ width: 150 }} />
			</Grid>
			<Grid item sm={3}>
				<ImageInput label="החלפת לוגו" source="picture">
					<ImageField source="src" />
				</ImageInput>
			</Grid>

			{/* GH-766 — banner image + "הצג באנר פרטי" moved to the "באנר" tab (CompanyBannerTab) */}
			<Box width="100%" />

			<Grid item sm={2}>
				<BooleanInput label=" חברה גלובלית? " fullWidth source="is_global" />
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label="הסתר תובנות AI" fullWidth source="dont_show_ai" />
			</Grid>

			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
					טקסט פתיחה לאונבורדינג (ריק = הטקסט הכללי)
				</Typography>
			</Grid>
			<Grid item xs={12} md={6}>
				<TextInput source="onboarding_welcome_text.he" label="עברית" fullWidth multiline minRows={4} />
			</Grid>
			<Grid item xs={12} md={6}>
				<TextInput source="onboarding_welcome_text.en" label="אנגלית" fullWidth multiline minRows={4} dir="ltr" />
			</Grid>

			{/* GH-740 — company disclaimer popped at the start of onboarding (the KPMG-style modal); the member
			    must tick "אני מבין/ה ומאשר/ת" to continue. Was DB-only until now. */}
			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
					דיסקליימר בתחילת ההרשמה
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<BooleanInput label="הצגת דיסקליימר בתחילת ההרשמה (חובה לאשר כדי להמשיך)" fullWidth source="company_special_terms" />
			</Grid>
			<Grid item xs={12} md={6}>
				<TextInput source="company_special_terms_text" label="טקסט הדיסקליימר — עברית" fullWidth multiline minRows={4} />
			</Grid>
			<Grid item xs={12} md={6}>
				<TextInput source="company_special_terms_text_en" label="Disclaimer text — English" fullWidth multiline minRows={4} dir="ltr" />
			</Grid>

			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
					מתחמים
				</Typography>
			</Grid>
			<Grid item sm={2}>
				<BooleanInput source="helping_hand" label="עזרה ראשונה (Helping hand)" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="הזמנת בן משפחה" fullWidth source="invite_family_members" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="תכנית פרואקטיבית?" fullWidth source="allow_proactive_app" />
			</Grid>

			<FormDataConsumer>
				{({ formData }) =>
					!formData?.allow_proactive_app && (
						<Grid item sm={2}>
							<BooleanInput
								label="להראות שאלות שאין תכנית פרואקטיבית?"
								fullWidth
								source="show_onbording_in_not_allow_proactive_app"
							/>
						</Grid>
					)
				}
			</FormDataConsumer>
			<FormDataConsumer>
				{({ formData }) =>
					formData?.allow_proactive_app && (
						<Grid item sm={2}>
							<BooleanInput
								label="בוסטים בלי שאלות אונבורדינג?"
								fullWidth
								source="skip_onboarding_questions"
							/>
						</Grid>
					)
				}
			</FormDataConsumer>
			<Grid item sm={2}>
				<BooleanInput label="פרגונים?" fullWidth source="pass_it_on" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="אזור אתגרים?" fullWidth source="challenges_enabled" />
			</Grid>
			{/* GH-662 — autonomous step challenge. Its own flag (not challenges_enabled) so older app
			    versions keep showing nothing; the engine opens a 14-day cycle on the 1st every N months. */}
			<Grid item sm={2}>
				<BooleanInput label="אתגר צעדים אוטונומי?" fullWidth source="step_challenge_enabled" />
			</Grid>
			{/* GH-778 — "אתגר חבר" add-on: members joined in a running cycle can invite a colleague to a
			    duel + joint team. Active only when step_challenge_enabled is on too (the server checks both). */}
			<Grid item sm={2}>
				<BooleanInput
					label="אתגר חבר (הזמנת חברים לתחרות האוטונומית)"
					helperText="פעיל רק כשאתגר צעדים אוטונומי דלוק"
					fullWidth
					source="step_invite_enabled"
				/>
			</Grid>
			{/* GH-813 — the app may also count steps from Apple Health / Health Connect (a watch): the "walking with
			    a watch?" card + the higher of phone / health store per day. Off (default) = phone only. */}
			<Grid item sm={2}>
				<BooleanInput
					label="ספירת צעדים מהשעון (Apple Health / Health Connect)"
					helperText="כבוי = רק מהטלפון"
					fullWidth
					source="health_store_steps_enabled"
				/>
			</Grid>
			{/* GH-826 — Android only: after each step sync the app re-asks Google Play Services to keep recording steps
			    (restarts a recording that stopped) and quietly reports it (member page → מכשירים). Off (default) =
			    the app behaves exactly as before. Turning it off takes effect on the member's next app open. */}
			<Grid item sm={2}>
				<BooleanInput
					label="בדיקת ספירת צעדים ברקע (אנדרואיד)"
					helperText="כבוי = כמו היום, בלי בדיקה ובלי דיווח"
					fullWidth
					source="step_recording_check_enabled"
				/>
			</Grid>
			<Grid item sm={2}>
				<NumberInput
					label="חודשים בין מחזורי אתגר צעדים"
					helperText="ברירת מחדל 1 (מחזור של 14 יום ב-1 לחודש)"
					fullWidth
					source="step_challenge_months_between_cycles"
					min={1}
				/>
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="התערבויות פרואקטיביות? (פיילוט)" fullWidth source="proactive_interventions_enabled" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="מייל יום הולדת ל-HR (3 ימים מראש)" fullWidth source="birthdayHappyProcess" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="הצגת מתחם L&D ל-HR?" fullWidth source="showLearningSection" />
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label="הראה life style" fullWidth source="show_life_style" />
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label="הסתר vod" fullWidth source="hide_vod" />
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label="הסתר הרצאות" fullWidth source="hide_lectures" />
			</Grid>

			<Grid item sm={4}>
				{/* 15.9.2026 (מכבי): בוסטים רק מהקטגוריות האלה (ריק = התבנית הכללית). */}
				<ReferenceArrayInput reference="proactive/item/category" perPage={999} source="boosts_categories">
					<SelectArrayInput optionText="name" label="קטגוריות בוסטים לחברה (ריק = הכל)" fullWidth helperText="כשמוגדר, הבוסטים השבועיים נבחרים רק מהקטגוריות האלה" />
				</ReferenceArrayInput>
			</Grid>

			<Grid item sm={4}>
				{/* GH-711 (מכבי, 22.9.2026): החברה רואה רק את הפרסים האלה (ריק = כל הפרסים כמו היום). הפרסים עצמם נשארים גלויים לשאר החברות. */}
				<ReferenceArrayInput reference="prize" perPage={999} source="prizes" filter={{ visible: true }}>
					<AutocompleteArrayInput optionText={(r: any) => r?.title?.he || r?.title?.en || r?.id} label="פרסים לחברה (ריק = כל הפרסים)" fullWidth helperText="כשמוגדר, חברי החברה רואים רק את הפרסים האלה" />
				</ReferenceArrayInput>
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label="הסתר דייטינג" fullWidth source="remove_dating" />
			</Grid>

			<Grid item sm={2}>
				<BooleanInput label=" דייטינג בלבד! אם חיובי החברה מוגבלת לאזור דייטינג בלבד" fullWidth source="is_dating_only" />
			</Grid>

			{/* <Grid item sm={2}>
				<NumberInput source="sameHostIn3Months" label="מפגש מקסימלי עם אותו מארח ב-3 חודשים" fullWidth />
			</Grid>

			<Grid item sm={2}>
				<NumberInput source="sameMonthSameHostIn30Days" label="מפגש מקסימלי עם אותו מארח ב-1 חודשים" fullWidth />
			</Grid>

			<Grid item sm={2}>
				<NumberInput source="daysBetweenSameHost" label="הפרש בימים מפגישה לפגישה עם אותו מארח" fullWidth />
			</Grid> */}
			

			<Grid item sm={2}>
				<BooleanInput label="שיחות הכוונה?" fullWidth source="one_to_one_available" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="תוכניות" fullWidth source="show_programs" />
			</Grid>
			<Grid item sm={3} alignContent={'center'}>
				<SelectArrayInput
					style={{ marginTop: -10 }}
					fullWidth
					choices={[
						{ name: 'Dating', id: 'DATING' },
						{ name: 'Care Givers', id: 'CARE_GIVERS' },
						{ name: 'Cool Stuff', id: 'COOL_STUFF' },
						{ name: 'קשב וריכוז', id: 'ADHD' },
					]}
					source="imagebuttons_names"
				/>
			</Grid>

			<Box width="100%" />
			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
				יעוץ 1:1
				</Typography>
			</Grid>
			<Grid item sm={2}>
				<BooleanInput source="remove_follow_up" label=" הסר followups" />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput source="remove_extra_call" label=" הגבלה לשיחת הכוונה בחודש" />
			</Grid>
			<FormDataConsumer>
				{({ formData }) =>
					formData?.remove_extra_call && (
						<Grid item sm={2}>
							<BooleanInput
								source="allow_extra_guidance_requests"
								label=" אפשרות לבקש נוספות (אישור HR במייל)"
							/>
						</Grid>
					)
				}
			</FormDataConsumer>
			<Grid item sm={2}>
				<BooleanInput source="consultation_quarterly" label=" שיחת הכוונה אחת לרבעון" />
			</Grid>
			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
					מרחב בטוח
				</Typography>
			</Grid>
			<Grid item sm={2}>
				<BooleanInput source="safe_space" label="המרחב הבטוח" />
			</Grid>
			<Grid item sm={2}>
				<NumberInput source="safe_space_usage_limit" label="בנק שעות מרחב בטוח" inputProps={{}} min={0} />
			</Grid>

			<Grid item sm={3}>
				<BooleanInput source="safe_space_for_family_members" label="הצגת מרחב בטוח לבני משפחה" />
			</Grid>

			<Grid item sm={3}>
				<BooleanInput source="safe_space_for_family_members_no_subscription" label="מחיר רגיל למרחב בטוח לבני משפחה" />
			</Grid>
			<Box width="100%" />
			<Grid item sm={3}>
				<BooleanInput source="family_plan.enabled" label="תכנית משפחה (הזמנת עד N + בנק מרחב בטוח משותף לתא)" />
			</Grid>
			<Grid item sm={2}>
				<NumberInput source="family_plan.max_members" label="מקס' בני משפחה להזמנה" min={1} />
			</Grid>
			<Grid item sm={3}>
				<BooleanInput source="family_plan.independent_pools" label="סבסוד עצמאי לכל מוזמן (בלי בנק משותף)" />
			</Grid>
			<Box width="100%" />
			<Grid item sm={4}>
				<TextInput fullWidth source="safe_space_text_lng.he" label="טקסט מרחב בטוח" multiline />
			</Grid>
			<Grid item sm={4}>
				<TextInput fullWidth source="safe_space_text_lng.en" label="טקסט מרחב בטוח (אנ')" multiline />
			</Grid>

			<Box width="100%" />
			<Grid item xs={12}>
				<Typography variant="h6" textAlign={'start'}>
					טקסט פתיחה
				</Typography>
			</Grid>
			<Grid item sm={12}>
				<TextInput fullWidth source="hello_text" label="כותרת לטקסט פתיחה" />
			</Grid>
			<Grid item sm={12}>
				<TextInput fullWidth source="text" label="טקסט פתיחה" multiline />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput label="שליחת טקסט פתיחה?" fullWidth source="send_welcome_text" />
			</Grid>

			<Box width="100%" />
			<Grid item sm={3}>
				<ArrayInput source="safe_space_prices" label="מחירי מרחב בטוח">
					<SimpleFormIterator
						disableClear
						fullWidth
						getItemLabel={(index: number) => <Typography width={60}>{`${index + 1}. `}</Typography>}
					>
						<NumberInput source="price" fullWidth />
					</SimpleFormIterator>
				</ArrayInput>
			</Grid>
			<Box width="100%" />
			<Grid item sm={2}>
				<DateInput source="subscription_start" label="תחילת המנוי" />
					<SafeSpaceResetField />
			</Grid>
			<Grid item sm={2}>
				<BooleanInput source="active" label="חברה פעילה" />
			</Grid>
			<Grid item xs={12} sx={{ textAlign: 'right' }}>
				<UsageBenchmarkGauge data={record?.computedUsage} />
			</Grid>
			<Grid item sm={12}>
				<div className="text-start">
					<Typography variant="h6" gutterBottom sx={{ backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: 1, display: 'inline-block' }}>פקטורים להשלמת תצוגת נתונים רבעונית</Typography>
					{udiFactors ? (
						<table className="table-fixed border-separate border-spacing-x-5">
							<thead>
								<th className="text-start w-44 ">שם</th>
								<th className="text-start">ערך</th>
								<th className="text-start w-32"></th>
								<th className="text-start"></th>
							</thead>
							{Object.entries(udiFactors).map(([key, valueObj]) => (
								<tr className="h-8">
									<td className={`text-start text-sm ${!valueObj.active ? 'line-through' : ''}`}>
										{key ?? 0}
									</td>
									<td
										className={`text-start text-sm font-bold ${
											!valueObj.active ? 'line-through' : ''
										}`}
									>
										{valueObj.value}
									</td>
									<td dir="ltr" className="text-left text-xs">
										{moment(valueObj.updatedAt).fromNow()}
									</td>
									<td className="">
										<Button
											disabled={!valueObj.active}
											variant="text"
											onClick={async () => {
												axios.delete(
													`${constants.API_URL}/company/${record._id}/udi_deactivate_factor`,
													{
														data: { name: key },
													}
												);

												refetch();
											}}
										>
											<GiCancel />
										</Button>
									</td>
								</tr>
							))}
						</table>
					) : null}
					<div className="">
						<Button variant="contained" className="block" onClick={onAddUdiFactorsButtonPress}>
							עריכת פקטורים
						</Button>
					</div>
				</div>
			</Grid>
		</Grid>
	);
};

export default GeneralCompanyInfo;
